// LooksPostForm.js

import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  collection,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import { LooksPostsContext } from '../contexts/LooksPostsContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoMdCloudUpload, IoMdImages } from 'react-icons/io';
import {
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaFacebook,
  FaSoundcloud
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';   // ← NEW X / Twitter icon
import CloseButton from './CloseButton';
import {
  uploadImageToStorage,
  savePostSummary,
  checkFileValidity as originalValidateFileType
} from '../utils/dbUtilities';
import heic2any from 'heic2any';
import './PostForm.css';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import axios from 'axios';

/* ── Ice-Breaker prompts (add just below the imports) ───────────── */
export const ICE_BREAKER_QUESTIONS = [
  { id: 'origin',             label: 'Where are you from?',                                             type: 'text'  },
  { id: 'favoriteFood',       label: "What's your favorite food?",                                      type: 'text'  },
  { id: 'favCountryVisit',    label: 'Favorite country you’ve visited?',                                type: 'text'  },
  { id: 'musicGenre',         label: 'Which song or artist have you been playing on repeat lately?',   type: 'text'  },
  { id: 'dreamMeet',          label: 'If you could meet anyone (dead or alive), who would it be?',     type: 'text'  },
  { id: 'favDessert',         label: "What's your favorite dessert?",                                   type: 'text'  },

  /* last prompt → two-option checkbox question */
  {
    id: 'millionOrBillion',
    label: 'Would you take $1 million guaranteed or flip a coin for $1 billion?',
    type: 'choice',
    options: ['Take the $1 million', 'Flip the coin']
  }
];

// =========== Constants & Utility Functions ===========

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const invalidFileTypes = ['video/mp4', 'audio/wav', 'image/gif', 'video/x-ms-wmv'];

// Slightly modified checkFileValidity so it can be used by both main & profile image dropzones
const checkFileValidity = (file, setError, setProcessing, setProgress) => {
  if (invalidFileTypes.includes(file.type)) {
    const errorMsg = 'Invalid file type. MP4, WAV, GIF, and WMV files are not allowed.';
    setError(errorMsg);
    setProcessing(false);
    setProgress(0);
    return false;
  }

  if (file.size > MAX_FILE_SIZE) {
    const errorMsg = 'File size exceeds 5 MB limit.';
    setError(errorMsg);
    setProcessing(false);
    setProgress(0);
    return false;
  }

  setError('');
  return true;
};

const compressImage = (file, setProgress) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, img.width, img.height);
      canvas.toBlob((blob) => {
        setProgress(70);
        resolve(blob);
      }, 'image/jpeg', 0.5);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const convertImageToJPG = async (file, setProgress) => {
  if (file.type === 'image/heic') {
    try {
      const convertedBlob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.92
      });
      setProgress(50);
      return convertedBlob;
    } catch (error) {
      console.error('Error converting image:', error);
      throw error;
    }
  } else {
    return compressImage(file, setProgress);
  }
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop, circular = false) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  canvas.width = safeArea;
  canvas.height = safeArea;

  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.translate(-safeArea / 2, -safeArea / 2);

  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.putImageData(
    data,
    0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x,
    0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y
  );

  if (circular) {
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      canvas.width / 2,
      0,
      2 * Math.PI,
      true
    );
    ctx.fill();
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg');
  });
};

// =========== Dropzone Components ===========
function MyDropzone({
  onImageUpload,
  setIsImageProcessing,
  setProgress,
  setUploadError,
  onImageChosen,
  onCropperOpen
}) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [croppedImagePreview, setCroppedImagePreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  useEffect(() => {
    // If the cropper is open (selectedImage && no croppedImagePreview), inform parent
    if (selectedImage && !croppedImagePreview) {
      onCropperOpen(true);
    } else {
      onCropperOpen(false);
    }
  }, [selectedImage, croppedImagePreview, onCropperOpen]);

  const handleSave = async (event) => {
    event.preventDefault();
  
    try {
      /* ① crop to a circle (avatar) */
      const circleBlob = await getCroppedImg(
        selectedImage,
        croppedAreaPixels,
        /* circular = */ true
      );
  
      /* ② grab the *square* version the user originally picked      *
       *    (selectedImage is a browser blob URL)                    */
      const resp        = await fetch(selectedImage);
      const squareBlob  = await resp.blob();      // <- original aspect
  
      /* ③ preview the avatar to the user (keeps old behaviour) */
      const circleUrl = URL.createObjectURL(circleBlob);
      setCroppedImagePreview(circleUrl);
      setSelectedImage(null);
  
      /* ④ send *both* blobs upward */
      onImageUpload({
        circle: circleBlob,     // avatar (used just like before)
        square: squareBlob      // NEW – full frame
      });
  
      /* ⑤ housekeeping */
      setIsImageProcessing(false);
      setProgress(100);
    } catch (error) {
      console.error('Error cropping image:', error);
      setIsImageProcessing(false);
      setProgress(0);
    }
  };

  const handleCancel = () => {
    setSelectedImage(null);
    setCroppedImagePreview(null);
    setOriginalImage(null);
    onImageChosen(false);

    // Also mark image processing as done if user cancels
    setIsImageProcessing(false);
    setProgress(0);
  };

  const handleEdit = (event) => {
    event.preventDefault();
    setSelectedImage(originalImage);
    setCroppedImagePreview(null);
  };

  const onDrop = async (acceptedFiles) => {
    setIsImageProcessing(true);
    setProgress(5);

    const file = acceptedFiles[0];
    if (!checkFileValidity(file, setUploadError, setIsImageProcessing, setProgress)) {
      return;
    }

    onImageChosen(true);

    try {
      const imageBlob =
        file.type === 'image/heic'
          ? await convertImageToJPG(file, setProgress)
          : await compressImage(file, setProgress);
      const imageUrl = URL.createObjectURL(imageBlob);

      setOriginalImage(imageUrl);
      setSelectedImage(imageUrl);
      setCroppedImagePreview(null);
      setProgress(80);
    } catch (error) {
      console.error('Error in onDrop image conversion:', error);
      // Also reset if there's an error
      setIsImageProcessing(false);
      setProgress(0);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/*',
    onDrop
  });

  return (
    <div className="image-upload-section">
      {!selectedImage && !croppedImagePreview && (
        <div {...getRootProps()} className="drag-drop-zone">
          <input {...getInputProps()} />
          <IoMdCloudUpload size={50} className="upload-icon" />
          <p>Drag & Drop or Click Here to Upload Image (5 MB Size Limit)</p>
          <IoMdImages size={50} className="images-icon" />
        </div>
      )}

      {croppedImagePreview && (
        <div className="image-preview-container">
          <img
            src={croppedImagePreview}
            alt="Cropped"
            className="uploaded-image-preview"
          />
          <button onClick={handleCancel} className="remove-image-icon">
            X
          </button>
          <button onClick={handleEdit} className="edit-button">
            Edit
          </button>
        </div>
      )}

      {selectedImage && !croppedImagePreview && (
        <div className="cropper-container">
          <Cropper
            image={selectedImage}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            cropShape="rect"
            showGrid={false}
            style={{
              containerStyle: {
                position: 'relative',
                width: '100%',
                height: '400px'
              },
              cropAreaStyle: {
                border: '2px solid white'
              },
              mediaStyle: {
                transform: 'translateZ(0)'
              }
            }}
          />
          <div className="cropper-toolbar">
            <button type="button" onClick={handleSave} className="save-button">
              Save
            </button>
            <button type="button" onClick={handleCancel} className="cancel-button">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
   PROFILE-IMAGE DROPZONE  (New version – saves BOTH circle and square blobs)
   ========================================================================*/
   function ProfileImageDropzone({
  onImageUpload,
  setIsImageProcessing,
  setProgress,
  setUploadError
}) {
  const [selectedImage,       setSelectedImage]       = useState(null);
  const [originalImage,       setOriginalImage]       = useState(null);
  const [croppedImagePreview, setCroppedImagePreview] = useState(null);
  const [crop,  setCrop]  = useState({ x: 0, y: 0 });
  const [zoom,  setZoom]  = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  /* ---------- cropper callbacks ---------- */
  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  /* ---------- SAVE ⇒ return {circle,square} ---------- */
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      // a) circular avatar
      const circleBlob = await getCroppedImg(
        selectedImage,
        croppedAreaPixels,
        /* circular */ true
      );

      // b) full-frame square  (bytes straight from the original)
      const resp       = await fetch(selectedImage);
      const squareBlob = await resp.blob();

      // c) preview & bubble up
      setCroppedImagePreview(URL.createObjectURL(circleBlob));
      setSelectedImage(null);
      onImageUpload({ circle: circleBlob, square: squareBlob });

      setIsImageProcessing(false);
      setProgress(100);
    } catch (err) {
      console.error('Error cropping image:', err);
      setIsImageProcessing(false);
      setProgress(0);
    }
  };

  const handleCancel = () => {
    setSelectedImage(null);
    setCroppedImagePreview(null);
    setOriginalImage(null);
    setIsImageProcessing(false);
    setProgress(0);
  };

  const handleRemove = () => {
    setSelectedImage(null);
    setCroppedImagePreview(null);
    setOriginalImage(null);
    onImageUpload({ circle: null, square: null });
    // no longer prefilling from localStorage, so we also stop persisting it
    localStorage.removeItem('profileImageUrl');
    setIsImageProcessing(false);
    setProgress(0);
  };

  /* ---------- onDrop: keep UN-cropped browser blob ---------- */
  const onDrop = async (acceptedFiles) => {
    setIsImageProcessing(true);
    setProgress(5);

    const file = acceptedFiles[0];
    if (!checkFileValidity(file, setUploadError, setIsImageProcessing, setProgress)) return;

    const imageBlob =
      file.type === 'image/heic' ? await convertImageToJPG(file, setProgress)
                                 : await compressImage(file, setProgress);

    const imageUrl = URL.createObjectURL(imageBlob);
    setOriginalImage(imageUrl);
    setSelectedImage(imageUrl);
    setCroppedImagePreview(null);
    setProgress(80);
  };

  const { getRootProps, getInputProps } = useDropzone({ accept: 'image/*', onDrop });

  /* ---------- JSX ---------- */
  return (
    <div className="image-upload-section profile-image">
      {/* --- 1. idle / no image --- */}
      {!selectedImage && !croppedImagePreview && (
        <div {...getRootProps()} className="drag-drop-zone circular">
          <input {...getInputProps()} />
          <IoMdCloudUpload size={50} className="upload-icon" />
          <p>Upload Profile Image&nbsp;(5 MB&nbsp;max)</p>
          <IoMdImages size={50} className="images-icon" />
        </div>
      )}

      {/* --- 2. preview ready --- */}
      {croppedImagePreview && (
        <div className="image-preview-container circular">
          <img src={croppedImagePreview} alt="Avatar" className="uploaded-image-preview" />
          <button onClick={handleRemove} className="remove-image-icon">×</button>
          <button
            onClick={() => { setSelectedImage(croppedImagePreview); setCroppedImagePreview(null); }}
            className="edit-button"
          >
            Edit
          </button>
        </div>
      )}

      {/* --- 3. cropping --- */}
      {selectedImage && !croppedImagePreview && (
        <div className="cropper-wrapper circular">
          <div className="cropper-container circular">
            <Cropper
              image={selectedImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              style={{
                containerStyle: { width: '100%', height: '300px', position: 'relative' },
                cropAreaStyle : { border: '2px solid #fff', borderRadius: '50%' }
              }}
            />
          </div>
          <div className="cropper-toolbar">
            <button type="button" onClick={handleSave}   className="save-button">Save</button>
            <button type="button" onClick={handleCancel} className="cancel-button">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
 

// =========== Main Component ===========
const LooksPostForm = () => {
  const { currentUser, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Form fields
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [country, setCountry] = useState('');
  const [userLocation, setUserLocation] = useState('');
  const [link, setLink] = useState('');
  const [instagram, setInstagram] = useState('');
  const [twitter, setTwitter] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [youtube, setYoutube] = useState('');
  const [facebook, setFacebook] = useState('');
  const [soundcloud, setSoundcloud] = useState(''); 
  const [email, setEmail] = useState('');

  // Word counts
  const [wordCount, setWordCount] = useState(0);

  // Validation errors
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Goals
  const [goals, setGoals] = useState([]);

  // Profile image
  const [profileImageBlob, setProfileImageBlob] = useState(null);
  const [profileImageSquareBlob,  setProfileImageSquareBlob]  = useState(null);  // NEW – full frame
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileImageError, setProfileImageError]   = useState('');
  const [profileImageProgress, setProfileImageProgress] = useState(0);

  // ─── placeholder so helpers that still call setUploadError compile ───
  const [uploadError, setUploadError] = useState('');

  const [profileImageProcessing, setProfileImageProcessing] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState('');

  /* ── NEW: Ice-breaker answers object ───────────────────────────── */
  const [iceBreakers, setIceBreakers] = useState(() =>
  ICE_BREAKER_QUESTIONS.reduce((acc, q) => {
    acc[q.id] = '';
    return acc;
  }, {})
  );

  // CHANGED: `Your Name` is now required
  const [userName, setUserName] = useState('');

  // A "top-level" general error message
  const [generalError, setGeneralError] = useState('');

  // Hooks/contexts
  const { addNewPost, updateSinglePost } = useContext(LooksPostsContext);

  // ===== Redirect if not logged in =====
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
    }
  }, [isLoggedIn, navigate]);

  // Word Count Helpers
  const updateWordCount = (text) => {
    const words = text.split(/\s+/).filter((word) => word.length > 0);
    setWordCount(words.length);
  };

  // Simple email validation
  const isValidEmail = (emailStr) => {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(emailStr).toLowerCase());
  };

  // Validate user-provided file type
  const validateFileType = (file) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Invalid file type. Only JPEG, PNG, HEIC, and HEIF images are allowed.');
      return false;
    }
    return true;
  };

  // Goals Setup
  useEffect(() => {
    const storedGoals = JSON.parse(localStorage.getItem('goals')) || [];
    setGoals(storedGoals);
  }, []);

  useEffect(() => {
    localStorage.setItem('goals', JSON.stringify(goals));
  }, [goals]);

  // ======== Handle Submit ========
const handleSubmit = async (event) => {
  event.preventDefault();
  setIsSubmitting(true);
  setShowErrors(true);

  /* ────────── reset any lingering UI state ────────── */
  setGeneralError('');

  try {
    /* ─────────────────── 1.  Gather form values ─────────────────── */
    const form          = event.target;
    const descriptionVal= form['description'].value.trim();
    const cityVal       = form['city'].value.trim();
    const stateVal2     = form['state'].value.trim();
    const countryVal    = form['country'].value.trim();
    const locationValue = form['location'].value;
    const linkVal       = form['link'].value.trim();
    const instagramVal  = form['instagram'].value.trim();
    const twitterVal    = form['twitter'].value.trim();
    const tiktokVal     = form['tiktok'].value.trim();
    const youtubeVal    = form['youtube'].value.trim();
    const facebookVal   = form['facebook'].value.trim();
    const userNameVal   = form['userName']?.value.trim();   // now required
    const emailVal      = form['email'].value.trim();

    /* ─────────────────── 2.  Validate form values ─────────────────── */
    let hasErrors = false;
    const newFieldErrors = {};

    /* ── existing required fields ──────────────────────────────────── */
    if (!descriptionVal) {
      newFieldErrors.description = 'Description is required.';
      hasErrors = true;
    }

    if (goals.length === 0) {
      newFieldErrors.goals = 'Please select at least one goal.';
      hasErrors = true;
    }

    if (!locationValue) {
      newFieldErrors.location = 'Please select a location.';
      hasErrors = true;
    }

    if (!countryVal) {
      newFieldErrors.country = 'Country is required.';
      hasErrors = true;
    }

    if (wordCount > 80) {
      newFieldErrors.description = 'Description cannot exceed 80 words.';
      hasErrors = true;
    }

    /* ── new required fields (Name & Bio) ──────────────────────────── */
    if (!userNameVal) {
      newFieldErrors.userName = 'Your Name is required.';
      hasErrors = true;
    }

    /* ── NOW REQUIRED: profile image ────────────────────────────── */
    if (!profileImageBlob) {               // user never hit “Save” in the cropper
      newFieldErrors.profileImage = 'Please upload a profile photo.';
      hasErrors = true;
    }

    /* ── optional URL & email validation ───────────────────────────── */
    if (
      linkVal &&
      !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/.test(linkVal)
    ) {
      newFieldErrors.link = 'Please enter a valid URL';
      hasErrors = true;
    }

    if (emailVal && !isValidEmail(emailVal)) {
      newFieldErrors.email = 'Please enter a valid email address.';
      hasErrors = true;
    }

    /* ── push validation results to state ──────────────────────────── */
    setFieldErrors(newFieldErrors);

    if (hasErrors) {
      setGeneralError('One or more required fields are missing or invalid.');
      setIsSubmitting(false);
      return;
    }


    // 4-b  Profile image
    let finalProfileImageUrl = '';   // avatar url (circle) – saved to post.profileImageUrl
    let fullProfileImageUrl  = '';   // NEW square url      – optional extra field

    /* upload square (original) first so you always keep it */
    if (profileImageSquareBlob) {
      fullProfileImageUrl = await uploadImageToStorage(
        profileImageSquareBlob,
        `looksProfileImagesFull/${currentUser.uid}_${Date.now()}`
      );
    }

    /* then upload the circle avatar (old behaviour) */
    if (profileImageBlob) {
      finalProfileImageUrl = await uploadImageToStorage(
        profileImageBlob,
        `looksProfileImagesAvatar/${currentUser.uid}_${Date.now()}`
      );
      localStorage.setItem('profileImageUrl', finalProfileImageUrl);
    }

    if (fullProfileImageUrl) {
      localStorage.setItem('profileImageFullUrl', fullProfileImageUrl);
    }    

    /* ───── NEW – strip empty ice-breaker answers ───── */
    const iceBreakersClean = Object.fromEntries(
      Object.entries(iceBreakers).filter(([, v]) => v.trim())
    );

    /* ─────────────────── 5.  Build Firestore payload ─────────────────── */
    const newPostData = {
      userId: currentUser.uid,
      description: descriptionVal,
      city: cityVal,
      state: stateVal2,
      country: countryVal,
      location: locationValue,
      goals,
      link: linkVal,
      instagram: instagramVal,
      twitter: twitterVal,
      tiktok: tiktokVal,
      youtube: youtubeVal,
      facebook: facebookVal,
      soundcloud: soundcloud, 
      userName: userNameVal,
      profileImageUrl: finalProfileImageUrl,
      profileImageFullUrl    : fullProfileImageUrl || null, // NEW – keep the master
      author: currentUser.displayName || 'Anonymous',
      timestamp: serverTimestamp(),
      showInDiscoverBox: true,
      email: emailVal || null,

      /* 👉 NEW  */
      iceBreakers: iceBreakersClean,

      /* analytics counters (unchanged) */
      upvotes: 0,
      viewCount: 0,
      linkClickCount: 0,
      impressionCount: 0,
      instagramClickCount: 0,
      twitterClickCount: 0,
      tiktokClickCount: 0,
      youtubeClickCount: 0,
      facebookClickCount: 0,
      discoveryBoxImpressionCount: 0
    };

    /* ─────────────────── 6.  Save to Firestore ─────────────────── */
    const docRef = await addDoc(collection(db, 'looksPosts'), newPostData);
    const optimisticPost = { ...newPostData, id: docRef.id, timestamp: new Date() };
    addNewPost(optimisticPost);

    /* ─────────────────── 7.  Generate summary (unchanged helper) ─────────────────── */
    await generateSummary(
      descriptionVal,
      docRef,
      setError,
      navigate,
      resetForm,
      optimisticPost,
      setIsSubmitting,
      updateSinglePost
    );
  } catch (error) {
    console.error('Error saving post:', error);
    setError('Error creating post. Please try again.');
    setIsSubmitting(false);
  }
};

  // ===== Summaries / AI call =====
  const generateSummary = async (
    descriptionVal,
    docRef,
    setError,
    navigate,
    resetForm,
    optimisticPost,
    setIsSubmitting,
    updateSinglePost
  ) => {
    const apiKey = process.env.REACT_APP_OPENAI_KEY;
    if (!apiKey) {
      console.log('OpenAI API key not found');
      // If there's no API key, just finalize without summary
      return finalizePost();
    }

    try {
      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content:
                'You are an assistant that generates concise summaries that closely resemble the original text.'
            },
            {
              role: 'user',
              content: `Summarize the following text in 8-10 words as a complete sentence, capturing the essence of the original text:\n\n${descriptionVal}`
            }
          ],
          max_tokens: 20,
          temperature: 0.7,
          n: 1,
          stop: ['\n']
        },
        {
          headers: {
            Authorization: `Bearer ${apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data?.choices?.[0]) {
        const generatedSummary = response.data.choices[0].message.content.trim();
        // Save summary in Firestore
        await savePostSummary(docRef.id, generatedSummary);

        // Update local state
        updateSinglePost({
          ...optimisticPost,
          summary: generatedSummary
        });
      }

      finalizePost();
    } catch (error) {
      console.error('Error generating post summary:', error);
      // Show a soft error, but do not block post creation
      setError(
        'Unable to generate a summary at this time, but your post was created successfully.'
      );
      finalizePost();
    }

    function finalizePost() {
      setIsSubmitting(false);
      localStorage.removeItem('description');

      resetForm();
      navigate(`/looksposts/${docRef.id}`);
    }
  };

    // ===== Reset Form =====
    const resetForm = () => {
      setDescription('');
      setCity('');
      setStateVal('');
      setCountry('');
      setUserLocation('');
      setLink('');
      setInstagram('');
      setTwitter('');
      setTiktok('');
      setYoutube('');
      setFacebook('');
      setSoundcloud('');  
      setGoals([]);

      /* 🔄 clear ice-breaker fields */
      setIceBreakers(() =>
        ICE_BREAKER_QUESTIONS.reduce((acc, q) => {
          acc[q.id] = '';
          return acc;
        }, {})
      );

      setUserName('');
      setEmail('');
      setProfileImageBlob(null);
      setProfileImageSquareBlob(null);
      setIsSubmitting(false);
    };

    const handleClose = () => {
      resetForm();
      navigate(-1);
    };
      
    if (!isLoggedIn) {
      return null;
    }
  
    return (
      <div className="form-container" style={{ marginTop: '150px', backgroundColor: 'white' }}>
        <CloseButton onClick={handleClose} />
        <div className="form-header">
          <h2 className="form-title" style={{ color: 'black' }}>
            New Post
          </h2>
        </div>
  
        <form className="post-form" onSubmit={handleSubmit}>
          
          {/* ====== Name ====== */}
          <div className="form-group">
            <label style={{ color: 'black' }}>What’s your name?</label>

            <input
              type="text"
              name="userName"
              className={`form-input ${
                showErrors && fieldErrors.userName ? 'error' : ''
              }`}
              placeholder="First Name, Nickname, Full Name — You Choose!"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />

            {showErrors && fieldErrors.userName && (
              <p className="error-message">{fieldErrors.userName}</p>
            )}
          </div>

          {/* ====== Profile Image Section ====== */}
          <h3 style={{ color: 'black', marginBottom: '8px' }}>Profile Image</h3>
          <ProfileImageDropzone
            onImageUpload={(payload) => {
              /* ------------------------------------------------------------------
                payload can be:
                ① { circle: Blob, square: Blob }  – new dual-image object
                ② Blob                            – legacy single avatar
                ③ null / {circle:null,square:null}– remove/reset
              ------------------------------------------------------------------ */
              if (payload && typeof payload === 'object' && 'circle' in payload) {
                setProfileImageBlob(payload.circle);
                setProfileImageSquareBlob(payload.square);
              } else if (payload instanceof Blob) {
                setProfileImageBlob(payload);
                setProfileImageSquareBlob(null);
              } else {
                setProfileImageBlob(null);
                setProfileImageSquareBlob(null);
              }
            }}
            setIsImageProcessing={setProfileImageProcessing}
            setProgress={setProfileImageProgress}
            setUploadError={setProfileImageError}
          />

          {showErrors && fieldErrors.profileImage && (
            <p className="error-message">{fieldErrors.profileImage}</p>
          )}

          {profileImageProcessing && (
            <div className="progress-bar-container">
              <progress value={profileImageProgress} max="100"></progress>
            </div>
          )}

          <p
            style={{
              marginTop: '16px',
              fontSize: '0.95rem',
              color: '#555',
              fontStyle: 'italic',
              width: '70%',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}
          >
            Add a Photo of You to Boost Post Views!
          </p>
  
          {/* ====== Description ====== */}
          <div className="form-group">
            <label style={{ color: 'black' }}>
              Tell us a little about yourself&nbsp;
              <span style={{ fontWeight: 400 }}>
                (Need&nbsp;help?&nbsp;
                <a
                  href="https://chatgpt.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1a0dab', textDecoration: 'none' }}
                >
                  Use ChatGPT!
                </a>)
              </span>
            </label>

            <textarea
              name="description"
              className={`form-input ${
                showErrors && fieldErrors.description ? 'error' : ''
              }`}
              rows="4"
              placeholder="Briefly introduce yourself. What should we know about you? 80 words max."
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                updateWordCount(e.target.value);
              }}
            />

            <p className={`word-count ${wordCount > 80 ? 'word-count-exceeded' : ''}`}>
              {wordCount} / 80 words
            </p>

            {showErrors && fieldErrors.description && (
              <p className="error-message">{fieldErrors.description}</p>
            )}
  
          </div>
  
          {/* ====== Goals ====== */}
          <div className="form-group goals-group">
            <label style={{ color: 'black' }}>Select Your Goal(s)</label>

            <div className="form-options">
              {[
                'Introduce Myself',
                'Grow My Audience',
                'Increase My Following Across Socials',
                'Increase My Online Presence',
                'Build Awareness',
                'Expand My Network', 
                'Reach New People',      
                'Get Constructive Feedback',
                'Have Fun!'
              ].map((goal) => (
                <div key={goal}>
                  <input
                    type="checkbox"
                    id={goal.toLowerCase().replace(/\s+/g, '-')}
                    name="goals"
                    value={goal}
                    checked={goals.includes(goal)}
                    onChange={(e) => {
                      const { value, checked } = e.target;
                      if (checked) {
                        setGoals((prev) => [...prev, value]);
                      } else {
                        setGoals((prev) => prev.filter((g) => g !== value));
                      }
                    }}
                  />
                  <label
                    htmlFor={goal.toLowerCase().replace(/\s+/g, '-')}
                    style={{ fontSize: '0.95rem' }}  
                  >
                    {goal}
                  </label>
                </div>
              ))}
            </div>

            {showErrors && fieldErrors.goals && (
              <p className="error-message">{fieldErrors.goals}</p>
            )}
          </div>

          {/* ====== Ice-Breaker Questions ====== */}
          <div className="form-group">
            <label style={{ color: 'black' }}>Fun Facts About You (Optional)</label>

            {ICE_BREAKER_QUESTIONS.map((q) => (
              <div key={q.id} style={{ marginBottom: '16px' }}>
                {/* ------------- TEXT INPUTS ------------- */}
                {q.type === 'text' && (
                  <input
                    type="text"
                    className="form-input"
                    placeholder={q.label}
                    value={iceBreakers[q.id]}
                    onChange={(e) =>
                      setIceBreakers((prev) => ({ ...prev, [q.id]: e.target.value }))
                    }
                  />
                )}

                {/* ------------- TWO-CHOICE QUESTION ------------- */}
                {q.type === 'choice' && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',           /* allows wrap on very small screens */
                      gap: '12px'
                    }}
                  >
                    {/* left-aligned, larger label */}
                    <span style={{ fontSize: '1.05rem' }}>{q.label}</span>

                    {/* the two inline check-boxes */}
                    {q.options.map((opt) => (
                      <label
                        key={opt}
                        style={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}
                      >
                        <input
                          type="checkbox"
                          checked={iceBreakers[q.id] === opt}
                          onChange={() =>
                            setIceBreakers((prev) => ({
                              ...prev,
                              [q.id]: prev[q.id] === opt ? '' : opt
                            }))
                          }
                        />
                        <span style={{ marginLeft: '4px' }}>{opt}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
  
          {/* ====== Link (Optional) ====== */}
          <div className="form-group">
            <label style={{ color: 'black' }} title="Add a link to your website">
              Add a URL Link (Optional)
            </label>
            <input
              type="text"
              name="link"
              className={`form-input ${showErrors && fieldErrors.link ? 'error' : ''}`}
              placeholder="e.g., example.com"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
            {showErrors && fieldErrors.link && (
              <p className="error-message">{fieldErrors.link}</p>
            )}
          </div>
  
          {/* ====== Email (Optional) ====== */}
          <div className="form-group">
            <label style={{ color: 'black' }} title="Provide a contact email (optional)">
              Add Your Contact Email (Optional)
            </label>
            <input
              type="email"
              name="email"
              className={`form-input ${showErrors && fieldErrors.email ? 'error' : ''}`}
              placeholder="e.g., your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {showErrors && fieldErrors.email && (
              <p className="error-message">{fieldErrors.email}</p>
            )}
          </div>
  
          {/* ====== Socials (Optional) ====== */}
          <div className="form-group">
            <label style={{ color: 'black' }}>Share Your Socials (Optional)</label>
            <div className="social-inputs">
              <div className="social-input">
                <FaInstagram className="social-icon instagram" />
                <input
                  type="text"
                  name="instagram"
                  className="form-input"
                  placeholder="Instagram Username"
                  value={instagram}
                  onChange={(e) => setInstagram(e.target.value)}
                />
              </div>
              <div className="social-input">
                <FaXTwitter className="social-icon twitter" />
                <input
                  type="text"
                  name="twitter"
                  className="form-input"
                  placeholder="Twitter/X Username"
                  value={twitter}
                  onChange={(e) => setTwitter(e.target.value)}
                />
              </div>
              <div className="social-input">
                <FaTiktok className="social-icon tiktok" />
                <input
                  type="text"
                  name="tiktok"
                  className="form-input"
                  placeholder="TikTok Username"
                  value={tiktok}
                  onChange={(e) => setTiktok(e.target.value)}
                />
              </div>
              <div className="social-input">
                <FaYoutube className="social-icon youtube" />
                <input
                  type="text"
                  name="youtube"
                  className="form-input"
                  placeholder="YouTube Channel Handle"
                  value={youtube}
                  onChange={(e) => setYoutube(e.target.value)}
                />
              </div>
              <div className="social-input">
                <FaFacebook className="social-icon facebook" />
                <input
                  type="text"
                  name="facebook"
                  className="form-input"
                  placeholder="Facebook Username"
                  value={facebook}
                  onChange={(e) => setFacebook(e.target.value)}
                />
              </div>
              <div className="social-input">
                {/* 👇 add the .soundcloud class */}
                <FaSoundcloud
                  className="social-icon soundcloud"
                  /* or style={{ color:'#ff5500' }} if you prefer inline */
                />
                <input
                  type="text"
                  name="soundcloud"
                  className="form-input"
                  placeholder="SoundCloud Handle"
                  value={soundcloud}
                  onChange={(e) => setSoundcloud(e.target.value)}
                />
              </div>
            </div>
          </div>
  
          {/* ====== Location (Continent) ====== */}
          <div className="form-group location-group">
            <label style={{ color: 'black' }}>Where Are You Based?</label>

            <div className="form-options">
              {[
                'North America',
                'Europe',
                'Africa',
                'Middle East',
                'Asia',
                'Central America',
                'South America',
                'Australia'
              ].map((loc) => (
                <div key={loc}>
                  <input
                    type="radio"
                    id={loc.toLowerCase().replace(' ', '')}
                    name="location"
                    value={loc}
                    checked={userLocation === loc}
                    onChange={(e) => setUserLocation(e.target.value)}
                  />
                  <label htmlFor={loc.toLowerCase().replace(' ', '')}>{loc}</label>
                </div>
              ))}
            </div>

            {showErrors && fieldErrors.location && (
              <p className="error-message">{fieldErrors.location}</p>
            )}
          </div>

          {/* 🌎  Country (same row, full width) */}
          <div
            className="form-group"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}
          >
            <label
              style={{
                color: 'black',
                minWidth: '80px',
                paddingTop: '6px'   /* ↓ nudges text down a bit */
              }}
            >
              Country
            </label>
            <input
              type="text"
              name="country"
              className={`form-input ${showErrors && fieldErrors.country ? 'error' : ''}`}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
          {showErrors && fieldErrors.country && (
            <p className="error-message">{fieldErrors.country}</p>
          )}

          {/* 🏙️  City (Optional) — its own row */}
          <div
            className="form-group city-row"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}
          >
            <label
              style={{
                color: 'black',
                minWidth: '110px',
                paddingTop: '6px'   /* ↓ same vertical tweak */
              }}
            >
              City&nbsp;(Optional)
            </label>
            <input
              type="text"
              name="city"
              className="form-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
          {showErrors && fieldErrors.city && (
            <p className="error-message">{fieldErrors.city}</p>
          )}

          {/* 🏛️  State (Optional) — its own row */}
          <div
            className="form-group state-row"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}
          >
            <label
              style={{
                color: 'black',
                minWidth: '110px',
                paddingTop: '6px'   /* ↓ same vertical tweak */
              }}
            >
              State&nbsp;(Optional)
            </label>
            <input
              type="text"
              name="state"
              className="form-input"
              value={stateVal}
              onChange={(e) => setStateVal(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>

        {/* ====== The NEW general error block (above Post button) ====== */}
        {generalError && (
          <p className="error-message" style={{ textAlign: 'center', marginTop: '20px' }}>
            {generalError}
          </p>
        )}

        {/* ====== Post Button ====== */}
        <div className="form-footer">
          <button
            type="submit"
            className={`form-button ${isSubmitting ? 'disabled' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Posting…' : 'Post'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LooksPostForm;